import type { ProductionOrder } from '@/services/productionOrders'
import type { CompanyProfile } from '@/config/companyProfile'

export interface MilestoneDates {
  d_material_arrival?: string | null
  d_white_done?: string | null
  d_paint_done?: string | null
  d_factory_completion?: string | null
  d_shipment?: string | null
}

export const MILESTONE_SEQUENCE: Array<{ field: keyof MilestoneDates; label: string }> = [
  { field: 'd_material_arrival', label: 'NVL về' },
  { field: 'd_white_done', label: 'Xong mộc' },
  { field: 'd_paint_done', label: 'Xong sơn' },
  { field: 'd_factory_completion', label: 'Hoàn thành (nhà máy)' },
  { field: 'd_shipment', label: 'Giao (ETD)' },
]

export interface MilestoneValidationResult {
  valid: boolean
  invalidFields: Array<keyof MilestoneDates>
  message?: string
}

/**
 * Validates the chronological order of milestones:
 * NVL về <= Xong mộc trắng <= Xong sơn <= Hoàn thành nhà máy <= Giao (ETD).
 * Only compares populated dates.
 */
export function validateMilestoneOrder(dates: MilestoneDates): MilestoneValidationResult {
  const invalidFields = new Set<keyof MilestoneDates>()
  let message: string | undefined

  const populated = MILESTONE_SEQUENCE.filter((m) => {
    const val = dates[m.field]
    return typeof val === 'string' && val.trim().length > 0
  })

  for (let i = 0; i < populated.length - 1; i++) {
    const curr = populated[i]
    const next = populated[i + 1]
    const currVal = dates[curr.field]!
    const nextVal = dates[next.field]!

    if (currVal > nextVal) {
      invalidFields.add(curr.field)
      invalidFields.add(next.field)
      if (!message) {
        message = `${curr.label} (${currVal}) không được sau ${next.label} (${nextVal})`
      }
    }
  }

  return {
    valid: invalidFields.size === 0,
    invalidFields: Array.from(invalidFields),
    message,
  }
}

/**
 * Immutably moves an item from startIndex to endIndex in an array.
 */
export function reorderItems<T>(list: T[], startIndex: number, endIndex: number): T[] {
  if (
    startIndex < 0 ||
    startIndex >= list.length ||
    endIndex < 0 ||
    endIndex >= list.length ||
    startIndex === endIndex
  ) {
    return list
  }
  const result = Array.from(list)
  const [removed] = result.splice(startIndex, 1)
  result.splice(endIndex, 0, removed)
  return result
}

/**
 * Sorts production orders by d_factory_completion ascending.
 * Rows with earlier dates come first.
 * Null/empty completion dates are placed at the end.
 * Preserves relative order of rows with equal completion dates (stable sort).
 */
export function sortRowsByFactoryCompletion(rows: ProductionOrder[]): ProductionOrder[] {
  return rows
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const dateA = a.item.d_factory_completion?.trim() || null
      const dateB = b.item.d_factory_completion?.trim() || null

      if (dateA === dateB) {
        return a.index - b.index
      }
      if (!dateA) return 1
      if (!dateB) return -1
      return dateA.localeCompare(dateB)
    })
    .map((wrapper) => wrapper.item)
}

export interface StatusBadgeConfig {
  label: string
  className: string
  title?: string
}

/**
 * Returns label, styling classes and tooltip for production order status chips.
 */
export function getStatusBadgeInfo(
  status: string,
  daysLate?: number | null,
  daysUntilFactory?: number | null,
): StatusBadgeConfig {
  switch (status) {
    case 'done':
      return {
        label: 'Hoàn tất',
        className: 'bg-emerald-600 text-white border-emerald-700 font-bold',
        title: 'Đơn đã sản xuất xong',
      }
    case 'late': {
      const lateLabel = daysLate != null && daysLate > 0 ? `Trễ hạn (${daysLate} ngày)` : 'Trễ hạn'
      return {
        label: lateLabel,
        className: 'bg-rose-50 text-rose-800 border-rose-300 font-bold',
        title: `Quá hạn hoàn thành nhà máy ${daysLate ?? 0} ngày`,
      }
    }
    case 'at_risk': {
      const remaining = daysUntilFactory != null ? ` (còn ${daysUntilFactory} ngày)` : ''
      return {
        label: `Nguy cơ trễ${remaining}`,
        className: 'bg-amber-50 text-amber-900 border-amber-300 font-semibold',
        title: 'Còn dưới 3 ngày tới hạn hoàn thành nhà máy',
      }
    }
    case 'on_track':
      return {
        label: 'Đúng tiến độ',
        className: 'bg-emerald-50 text-emerald-800 border-emerald-300',
        title: 'Tiến độ sản xuất bình thường',
      }
    case 'unscheduled':
    default:
      return {
        label: 'Chưa xếp lịch',
        className: 'bg-slate-100 text-slate-600 border-slate-200',
        title: 'Chưa đặt ngày Hoàn thành (nhà máy)',
      }
  }
}

/**
 * Formats dimensions as pure number without any unit suffix.
 * Default is INCH. When unit is MM, multiplies by 25.4 and rounds cleanly.
 */
export function formatDimPure(
  value: number | null | undefined,
  unit: 'inch' | 'mm' = 'inch',
): string {
  if (value == null || Number.isNaN(Number(value))) return '—'
  const num = Number(value)
  if (unit === 'mm') {
    return String(Math.round(num * 25.4))
  }
  // Inch: formatted cleanly up to 2 decimals if fraction, or integer
  return Number.isInteger(num) ? String(num) : num.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * Filter company profile for printed reports:
 * Drops any fields containing placeholder patterns like "[Điền ...]".
 */
export function filterCompanyProfileForPrint(
  profile: CompanyProfile,
): {
  legalName: string
  brandName: string
  brandTagline?: string
  address?: string
  phone?: string
  email?: string
  fax?: string
} {
  const isPlaceholder = (val?: string) => !val || val.includes('[Điền')

  return {
    legalName: profile.legalName,
    brandName: isPlaceholder(profile.brandName) ? '' : profile.brandName,
    brandTagline: isPlaceholder(profile.brandTagline) ? undefined : profile.brandTagline,
    address: isPlaceholder(profile.address) ? undefined : profile.address,
    phone: isPlaceholder(profile.phone) ? undefined : profile.phone,
    email: isPlaceholder(profile.email) ? undefined : profile.email,
    fax: isPlaceholder(profile.fax) ? undefined : profile.fax,
  }
}

/**
 * Calculates current month + next 3 months for production planning.
 */
export function getFourProductionMonths(): Array<{ month: string; label: string; title: string }> {
  const months: Array<{ month: string; label: string; title: string }> = []
  const now = new Date()
  for (let i = 0; i < 4; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1)
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yy = String(d.getFullYear()).slice(-2)
    const yyyy = d.getFullYear()
    months.push({
      month: `${yyyy}-${mm}`,
      label: `${mm}/${yy}`,
      title: `Tháng ${mm}/${yyyy}`,
    })
  }
  return months
}

/** Planning page tab: inbox = unscheduled plan lines; orders = month-filtered. */
export type PlanningTabId = 'inbox' | 'orders'

/**
 * Resolve API `month` query for /api/production-orders.
 * Inbox tab always uses the existing `unscheduled` filter (no month picker).
 */
export function resolveProductionOrdersMonthFilter(
  tab: PlanningTabId,
  selectedMonth: string,
): string {
  return tab === 'inbox' ? 'unscheduled' : selectedMonth
}

/**
 * Default planning tab: open inbox when there is at least one unscheduled line.
 */
export function resolveDefaultPlanningTab(inboxCount: number): PlanningTabId {
  return inboxCount > 0 ? 'inbox' : 'orders'
}

